/**
 * Campaign create/edit form — audience, reward, schedule steps.
 *
 * Inline form (not dialog) — replaces CampaignsTab content when active.
 * Follows MonthlyCouponsTab form patterns.
 *
 * Feature 9 — OPS-1862 (Campaign Management System)
 */

import { useState, useEffect } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  useCreateCampaign,
  useUpdateCampaign,
  useCampaignDetail,
  useRewardsConfig,
} from "../api/rewardsAdminHooks";

const AUDIENCE_OPTIONS = [
  { value: "All Members", label: "สมาชิกทั้งหมด" },
  { value: "Birthday This Month", label: "วันเกิดเดือนนี้" },
  { value: "Specific Tier", label: "เทียร์เฉพาะ" },
  { value: "Low Retention", label: "ลูกค้าเสี่ยงหาย" },
] as const;

const REWARD_OPTIONS = [
  { value: "Bonus Points", label: "แต้มโบนัส" },
  { value: "Coupon", label: "คูปอง" },
  { value: "Point Multiplier", label: "คูณแต้ม (x2, x3...)" },
] as const;

interface CampaignFormProps {
  campaignName: string | null;
  onClose: () => void;
}

export function CampaignForm({ campaignName, onClose }: CampaignFormProps) {
  const isEdit = !!campaignName;
  const detailQuery = useCampaignDetail(campaignName ?? "", isEdit);
  const configQuery = useRewardsConfig();
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();

  const tiers = configQuery.data?.tiers ?? [];

  const [form, setForm] = useState({
    campaign_name: "",
    description: "",
    audience_type: "All Members",
    audience_tier: "",
    audience_min_orders: 0,
    audience_min_spent: 0,
    reward_type: "Bonus Points",
    reward_points: 0,
    reward_coupon_template: "",
    reward_multiplier: 2,
    start_date: "",
    end_date: "",
  });

  // Populate form from server data when editing
  useEffect(() => {
    if (isEdit && detailQuery.data) {
      const d = detailQuery.data;
      setForm({
        campaign_name: d.campaign_name,
        description: d.description,
        audience_type: d.audience_type,
        audience_tier: d.audience_tier,
        audience_min_orders: d.audience_min_orders,
        audience_min_spent: d.audience_min_spent,
        reward_type: d.reward_type,
        reward_points: d.reward_points,
        reward_coupon_template: d.reward_coupon_template,
        reward_multiplier: d.reward_multiplier || 2,
        start_date: d.start_date ?? "",
        end_date: d.end_date ?? "",
      });
    }
  }, [isEdit, detailQuery.data]);

  const patch = (updates: Partial<typeof form>) =>
    setForm((prev) => ({ ...prev, ...updates }));

  const handleSubmit = () => {
    if (!form.campaign_name.trim()) {
      toast.error("กรุณาระบุชื่อแคมเปญ");
      return;
    }

    const payload = {
      ...form,
      reward_points: form.reward_type === "Bonus Points" ? form.reward_points : undefined,
      reward_coupon_template: form.reward_type === "Coupon" ? form.reward_coupon_template : undefined,
      reward_multiplier: form.reward_type === "Point Multiplier" ? form.reward_multiplier : undefined,
      audience_tier: form.audience_type === "Specific Tier" ? form.audience_tier : undefined,
      audience_min_orders: form.audience_type === "Low Retention" ? form.audience_min_orders : undefined,
      audience_min_spent: form.audience_type === "Low Retention" ? form.audience_min_spent : undefined,
    };

    if (isEdit) {
      updateMutation.mutate(
        { name: campaignName!, ...payload },
        {
          onSuccess: () => {
            toast.success("อัปเดตแคมเปญสำเร็จ");
            onClose();
          },
          onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (data) => {
          toast.success(
            data.status === "Scheduled"
              ? "สร้างแคมเปญสำเร็จ — กำหนดไว้ตามวันที่เริ่ม"
              : "สร้างแคมเปญสำเร็จ",
          );
          onClose();
        },
        onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEdit && detailQuery.isLoading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">
          {isEdit ? "แก้ไขแคมเปญ" : "สร้างแคมเปญใหม่"}
        </h3>
      </div>

      {/* Campaign name + description */}
      <div className="space-y-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>ชื่อแคมเปญ</Label>
          <Input
            placeholder="เช่น โปรวันเกิด มิถุนายน"
            value={form.campaign_name}
            onChange={(e) => patch({ campaign_name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>รายละเอียด (ไม่บังคับ)</Label>
          <Textarea
            placeholder="อธิบายแคมเปญสั้น ๆ"
            value={form.description}
            onChange={(e) => patch({ description: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {/* Audience section */}
      <div className="space-y-4 rounded-lg border p-4">
        <p className="text-sm font-medium">กลุ่มเป้าหมาย</p>
        <Select
          value={form.audience_type}
          onValueChange={(val) => patch({ audience_type: val })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUDIENCE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {form.audience_type === "Specific Tier" && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              ชื่อเทียร์
            </Label>
            {tiers.length > 0 ? (
              <Select
                value={form.audience_tier}
                onValueChange={(val) => patch({ audience_tier: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกเทียร์" />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map((t) => (
                    <SelectItem key={t.tier_name} value={t.tier_name}>
                      {t.tier_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">
                ยังไม่มีเทียร์ — กรุณาตั้งค่าเทียร์ในหน้าตั้งค่าโปรแกรมก่อน
              </p>
            )}
          </div>
        )}

        {form.audience_type === "Low Retention" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                ออเดอร์น้อยกว่า (12 เดือน)
              </Label>
              <Input
                type="number"
                min={0}
                value={form.audience_min_orders}
                onChange={(e) =>
                  patch({
                    audience_min_orders: Math.max(0, Number(e.target.value) || 0),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                ยอดใช้จ่ายน้อยกว่า (12 เดือน)
              </Label>
              <Input
                type="number"
                min={0}
                value={form.audience_min_spent}
                onChange={(e) =>
                  patch({
                    audience_min_spent: Math.max(0, Number(e.target.value) || 0),
                  })
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Reward section */}
      <div className="space-y-4 rounded-lg border p-4">
        <p className="text-sm font-medium">รางวัล</p>
        <Select
          value={form.reward_type}
          onValueChange={(val) => patch({ reward_type: val })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REWARD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {form.reward_type === "Bonus Points" && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              จำนวนแต้มที่ให้
            </Label>
            <Input
              type="number"
              min={1}
              value={form.reward_points}
              onChange={(e) =>
                patch({
                  reward_points: Math.max(1, Number(e.target.value) || 1),
                })
              }
            />
          </div>
        )}

        {form.reward_type === "Coupon" && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Coupon Code (ชื่อเทมเพลต)
            </Label>
            <Input
              placeholder="เช่น PROMO-JUNE-2026"
              value={form.reward_coupon_template}
              onChange={(e) =>
                patch({ reward_coupon_template: e.target.value })
              }
            />
          </div>
        )}

        {form.reward_type === "Point Multiplier" && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              ตัวคูณแต้ม (เช่น 2 = ได้แต้มคู่)
            </Label>
            <Input
              type="number"
              min={1.5}
              step={0.5}
              value={form.reward_multiplier}
              onChange={(e) =>
                patch({
                  reward_multiplier: Math.max(1.5, Number(e.target.value) || 2),
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              ลูกค้าจะได้แต้ม x{form.reward_multiplier} จากการซื้อสินค้าระหว่างช่วงแคมเปญ
            </p>
          </div>
        )}
      </div>

      {/* Schedule section */}
      <div className="space-y-4 rounded-lg border p-4">
        <p className="text-sm font-medium">กำหนดเวลา</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">วันเริ่มต้น</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => patch({ start_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">วันสิ้นสุด</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => patch({ end_date: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          ยกเลิก
        </Button>
        <Button onClick={handleSubmit} disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? "อัปเดต" : "สร้างแคมเปญ"}
        </Button>
      </div>
    </div>
  );
}
